import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { blogAPI } from '../../services/blogAPI';

interface BlogState {
  blogs: any[];
  selectedBlog: any | null;
  total: number;
  page: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;
}

const initialState: BlogState = {
  blogs: [],
  selectedBlog: null,
  total: 0,
  page: 1,
  totalPages: 0,
  isLoading: false,
  error: null,
};

export const fetchBlogs = createAsyncThunk('blogs/fetchBlogs', async (params: { page?: number; limit?: number; search?: string }) => {
  return await blogAPI.getAllBlogs(params);
});

export const fetchBlogDetails = createAsyncThunk('blogs/fetchBlogDetails', async (id: string) => {
  return await blogAPI.getBlogById(id);
});

export const createBlog = createAsyncThunk('blogs/createBlog', async (data: FormData) => {
  return await blogAPI.createBlog(data);
});

export const updateBlog = createAsyncThunk('blogs/updateBlog', async ({ id, data }: { id: string; data: FormData }) => {
  return await blogAPI.updateBlog(id, data);
});

export const deleteBlog = createAsyncThunk('blogs/deleteBlog', async (id: string) => {
  await blogAPI.deleteBlog(id);
  return id;
});

const blogSlice = createSlice({
  name: 'blogs',
  initialState,
  reducers: { clearError: (state) => { state.error = null; } },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBlogs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBlogs.fulfilled, (state, action) => {
        state.blogs = action.payload.data;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.isLoading = false;
      })
      .addCase(fetchBlogs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch blogs';
      })
      .addCase(fetchBlogDetails.fulfilled, (state, action) => {
        state.selectedBlog = action.payload.data;
        state.isLoading = false;
      })
      .addCase(createBlog.fulfilled, (state, action) => {
        state.blogs.unshift(action.payload.data || action.payload);
        state.total += 1;
      })
      .addCase(updateBlog.fulfilled, (state, action) => {
        const updatedBlog = action.payload.data || action.payload;
        const index = state.blogs.findIndex(b => b._id === updatedBlog._id);
        if (index !== -1) state.blogs[index] = updatedBlog;
      })
      .addCase(deleteBlog.fulfilled, (state, action) => {
        state.blogs = state.blogs.filter(b => b._id !== action.payload);
        state.total -= 1;
      });
  },
});

export default blogSlice.reducer;